import React, { useState, useEffect } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import userService from '../../services/userService';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import {
    Users,
    Plus,
    Search,
    Filter,
    Mail,
    Building2,
    Shield,
    UserCheck,
    GraduationCap,
    Truck,
    X,
    Eye,
    Edit2,
    AlertCircle,
    Trash2,
    RefreshCcw,
    Key
} from 'lucide-react';
import './UsersPage.css';

const UsersPage = () => {
    const { t, i18n } = useTranslation();
    const { isAdmin, isManager, isResponsable, user: currentUser, updateUser } = useAuth();
    const { canView, canCreate, canEdit, canDelete, loading: permissionsLoading } = usePermissions();
    const toast = useToast();
    const isMobile = useIsMobile();

    const allCentresLabel = i18n.exists('common.allCentres') ? t('common.allCentres') : 'Tous les centres';

    const canManageUsers = isAdmin || isManager;
    const canManageUsersAsAdmin = isAdmin;
    const [users, setUsers] = useState([]);
    const [responsables, setResponsables] = useState([]);
    const [responsablesCount, setResponsablesCount] = useState(0);
    const [adminUsers, setAdminUsers] = useState([]);
    const [adminUsersCount, setAdminUsersCount] = useState(0);
    const [fournisseurUsers, setFournisseurUsers] = useState([]);
    const [fournisseurUsersCount, setFournisseurUsersCount] = useState(0);
    const [comptableUsers, setComptableUsers] = useState([]);
    const [comptableUsersCount, setComptableUsersCount] = useState(0);
    const [logistiqueUsers, setLogistiqueUsers] = useState([]);
    const [logistiqueUsersCount, setLogistiqueUsersCount] = useState(0);
    const [managerUsers, setManagerUsers] = useState([]);
    const [managerUsersCount, setManagerUsersCount] = useState(0);
    const [centres, setCentres] = useState([]);
    const [fournisseurs, setFournisseurs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('responsables');
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [showInactive, setShowInactive] = useState(false);
    const [savingPreferences, setSavingPreferences] = useState({});

    const handleNotificationPreference = async (user, field, checked) => {
        setSavingPreferences(prev => ({ ...prev, [user.id]: true }));
        try {
            const updated = await userService.updateNotificationPreferences(user.id, { [field]: checked });
            [setUsers, setResponsables, setAdminUsers, setFournisseurUsers,
                setComptableUsers, setLogistiqueUsers, setManagerUsers].forEach(setList => {
                setList(list => list.map(item => item.id === user.id ? { ...item, ...updated } : item));
            });
            if (user.id === currentUser.id) updateUser(updated);
            toast.success(t('profile.notificationsSuccess'));
        } catch (error) {
            toast.error(t('profile.notificationsError'));
        } finally {
            setSavingPreferences(prev => ({ ...prev, [user.id]: false }));
        }
    };

    const renderNotificationPreferences = user => (
        <div className="user-notification-preferences">
            {[
                ['receive_notifications', 'receiveNotifications'],
                ['receive_email_notifications', 'receiveEmailNotifications'],
            ].map(([field, label]) => (
                <label key={field}>
                    <input type="checkbox" role="switch"
                        aria-label={`${t(`profile.${label}`)} — ${user.email}`}
                        checked={user[field] ?? true}
                        disabled={!!savingPreferences[user.id]}
                        onChange={event => handleNotificationPreference(user, field, event.target.checked)} />
                    <span>{t(field === 'receive_notifications' ? 'profile.inAppChannel' : 'profile.emailChannel')}</span>
                </label>
            ))}
        </div>
    );

    const [selectedCentre, setSelectedCentre] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 20;

    const canViewUsers = canView('users');

    const getDefaultRoleForTab = () => {
        if (isManager) {
            return 'BU_COORDINATOR';
        }
        if (!isAdmin) {
            return 'FORMATEUR';
        }
        switch (activeTab) {
            case 'admins':
                return 'ADMIN';
            case 'comptables':
                return 'COMPTABLE';
            case 'logistique':
                return 'LOGISTIQUE_COORDINATOR';
            case 'managers':
                return 'MANAGER';
            case 'fournisseurs':
                return 'FOURNISSEUR';
            case 'responsables':
            default:
                return 'BU_COORDINATOR';
        }
    };

    // Dialog states
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
    const [editingUserId, setEditingUserId] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [formError, setFormError] = useState('');
    const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
    const [hardDeleteConfirmUser, setHardDeleteConfirmUser] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        role: 'BU_COORDINATOR',
        centre: '',
        centres: [],
        all_centres: false,
        fournisseur: ''
    });

    // Get available centres for the current user
    const getAvailableCentres = () => {
        if (isAdmin) {
            return centres;
        }

        if (isManager) {
            if (currentUser?.centres_details && currentUser.centres_details.length) {
                return currentUser.centres_details;
            }
            if (currentUser?.centres && currentUser.centres.length) {
                return centres.filter(c => currentUser.centres.includes(c.id));
            }
            if (currentUser?.centre && centres.length) {
                return centres.filter(c => c.id === currentUser.centre);
            }
            return [];
        }

        if (currentUser?.centres_details && currentUser.centres_details.length) {
            return currentUser.centres_details;
        }

        if (currentUser?.centres && currentUser.centres.length) {
            return centres.filter(c => currentUser.centres.includes(c.id));
        }

        if (currentUser?.centre && centres.length) {
            return centres.filter(c => c.id === currentUser.centre);
        }

        return [];
    };

    const availableCentres = getAvailableCentres();
    const hasMultipleCentres = availableCentres.length > 1;
    const hasSingleCentre = availableCentres.length === 1;

    useEffect(() => {
        if ((isResponsable || isAdmin) && hasSingleCentre && !selectedCentre) {
            setSelectedCentre(availableCentres[0]?.id || '');
        }
    }, [isResponsable, isAdmin, hasSingleCentre, availableCentres, selectedCentre]);

    const fetchData = async () => {
        try {
            setLoading(true);
            console.log('Fetching centres...');
            const centresResponse = await userService.getCentres();
            console.log('Centres response:', centresResponse);
            // Handle both array and wrapped response
            const centresData = Array.isArray(centresResponse) ? centresResponse : centresResponse?.results || centresResponse?.centres || [];
            setCentres(centresData);

            // Fetch fournisseurs for the role select
            if (canManageUsersAsAdmin || isManager) {
                const fournisseursResponse = await userService.getFournisseurs();
                const fournisseursData = Array.isArray(fournisseursResponse) ? fournisseursResponse : fournisseursResponse?.results || [];
                setFournisseurs(fournisseursData);
            }

            const params = {
                page: currentPage,
                page_size: pageSize,
            };
            if (appliedSearchTerm) {
                params.search = appliedSearchTerm;
            }
            if (showInactive) {
                params.include_inactive = 'true';
            }

            if (selectedCentre && activeTab !== 'fournisseurs' && activeTab !== 'admins') {
                params.centre = selectedCentre;
            }

            if (canManageUsersAsAdmin) {
                if (activeTab === 'fournisseurs') {
                    console.log('Fetching fournisseurs users...');
                    const res = await userService.getAllUsers({ ...params, role: 'FOURNISSEUR' });
                    const data = Array.isArray(res) ? res : res?.results || [];
                    const count = (res && !Array.isArray(res) && typeof res.count === 'number') ? res.count : data.length;

                    setFournisseurUsers(data);
                    setFournisseurUsersCount(count);
                    setTotalCount(count);
                    setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
                } else if (activeTab === 'admins') {
                    console.log('Fetching admin users...');
                    const res = await userService.getAllUsers({ ...params, role: 'ADMIN' });
                    const data = Array.isArray(res) ? res : res?.results || [];
                    const count = (res && !Array.isArray(res) && typeof res.count === 'number') ? res.count : data.length;

                    setAdminUsers(data);
                    setAdminUsersCount(count);
                    setTotalCount(count);
                    setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
                } else if (activeTab === 'comptables') {
                    console.log('Fetching comptable users...');
                    const res = await userService.getAllUsers({ ...params, role: 'COMPTABLE' });
                    const data = Array.isArray(res) ? res : res?.results || [];
                    const count = (res && !Array.isArray(res) && typeof res.count === 'number') ? res.count : data.length;

                    setComptableUsers(data);
                    setComptableUsersCount(count);
                    setTotalCount(count);
                    setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
                } else if (activeTab === 'logistique') {
                    console.log('Fetching logistique users...');
                    const res = await userService.getAllUsers({ ...params, role: 'LOGISTIQUE_COORDINATOR' });
                    const data = Array.isArray(res) ? res : res?.results || [];
                    const count = (res && !Array.isArray(res) && typeof res.count === 'number') ? res.count : data.length;

                    setLogistiqueUsers(data);
                    setLogistiqueUsersCount(count);
                    setTotalCount(count);
                    setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
                } else if (activeTab === 'managers') {
                    console.log('Fetching manager users...');
                    const res = await userService.getAllUsers({ ...params, role: 'MANAGER' });
                    const data = Array.isArray(res) ? res : res?.results || [];
                    const count = (res && !Array.isArray(res) && typeof res.count === 'number') ? res.count : data.length;

                    setManagerUsers(data);
                    setManagerUsersCount(count);
                    setTotalCount(count);
                    setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
                } else {
                    console.log('Fetching responsables...');
                    const responsablesRes = await userService.getResponsables(params);
                    console.log('Responsables:', responsablesRes);

                    const responsablesData = Array.isArray(responsablesRes) ? responsablesRes : responsablesRes?.results || [];

                    const nextResponsablesCount = (responsablesRes && !Array.isArray(responsablesRes) && typeof responsablesRes.count === 'number')
                        ? responsablesRes.count
                        : responsablesData.length;

                    setResponsablesCount(nextResponsablesCount);
                    setResponsables(responsablesData);

                    setTotalCount(nextResponsablesCount);
                    setTotalPages(Math.max(1, Math.ceil(nextResponsablesCount / pageSize)));
                }
            } else if (isManager) {
                // Manager can list users via admin endpoint but should not see admins
                const res = await userService.getAllUsers(params);
                const data = Array.isArray(res) ? res : res?.results || [];
                const count = (res && !Array.isArray(res) && typeof res.count === 'number') ? res.count : data.length;
                setUsers(data.filter(u => u.role !== 'ADMIN'));
                setTotalCount(count);
                setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
            } else if (isResponsable) {
                console.log('Fetching responsables...');
                const responsablesRes = await userService.getResponsables(params);
                console.log('Responsables:', responsablesRes);
                const responsablesData = Array.isArray(responsablesRes) ? responsablesRes : responsablesRes?.results || [];
                setResponsables(responsablesData);

                const nextResponsablesCount = (responsablesRes && !Array.isArray(responsablesRes) && typeof responsablesRes.count === 'number')
                    ? responsablesRes.count
                    : responsablesData.length;
                setResponsablesCount(nextResponsablesCount);

                setTotalCount(nextResponsablesCount);
                setTotalPages(Math.max(1, Math.ceil(nextResponsablesCount / pageSize)));
            } else if (canViewUsers) {
                console.log('Fetching users (non-admin with permission)...');
                const res = await userService.getAllUsers(params);
                const data = Array.isArray(res) ? res : res?.results || [];
                const count = (res && !Array.isArray(res) && typeof res.count === 'number') ? res.count : data.length;

                setResponsables(data);
                setResponsablesCount(count);
                setTotalCount(count);
                setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            console.error('Error response:', error.response?.data);
            console.error('Error status:', error.response?.status);
        } finally {
            setLoading(false);
        }
    };

    // Auto-select centre if responsable has only one centre
    useEffect(() => {
        if ((isResponsable || isAdmin) && hasSingleCentre && !formData.centre) {
            setFormData(prev => ({
                ...prev,
                centre: availableCentres[0].id,
                centres: [availableCentres[0].id]
            }));
        }
    }, [isResponsable, hasSingleCentre, availableCentres, formData.centre]);

    // Fetch data on mount and when role changes
    useEffect(() => {
        console.log('Component mounted, fetching data...', { isAdmin, isResponsable });
        fetchData();
    }, [isAdmin, isResponsable, canViewUsers, activeTab, currentPage, appliedSearchTerm, showInactive, selectedCentre]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'centre') {
            setFormData(prev => ({ ...prev, centre: value, centres: value ? [value] : [] }));
            return;
        }
        if (name === 'role') {
            if (value === 'ADMIN') {
                setFormData(prev => ({ ...prev, role: value, centre: '', centres: [], all_centres: true, fournisseur: '' }));
                return;
            }
            setFormData(prev => ({ ...prev, role: value, centre: '', centres: [], all_centres: false, fournisseur: '' }));
            return;
        }
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAllCentresChange = (e) => {
        const checked = e.target.checked;
        setFormData(prev => ({
            ...prev,
            all_centres: checked,
            centres: checked ? [] : prev.centres,
            centre: checked ? '' : prev.centre,
        }));
    };

    const handleCentresChange = (e) => {
        const selected = Array.from(e.target.selectedOptions).map(o => o.value).filter(Boolean);
        setFormData(prev => ({
            ...prev,
            centres: selected,
            centre: selected[0] || ''
        }));
    };

    const handleCentresMultiSelectChange = (selected) => {
        setFormData(prev => ({
            ...prev,
            centres: selected,
            centre: selected[0] || ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');

        try {
            const payload = {
                ...formData,
                all_centres: formData.role === 'ADMIN' ? true : Boolean(formData.all_centres),
                centres: formData.centres?.length ? formData.centres : undefined,
            };
            if (canManageUsers) {
                if (editingUserId) {
                    await userService.updateUser(editingUserId, payload);
                } else {
                    await userService.createUser(payload);
                }
            } else if (isResponsable) {
                await userService.createUser(payload);
            }
            setIsAddDialogOpen(false);
            setEditingUserId(null);
            setFormData({
                first_name: '',
                last_name: '',
                email: '',
                role: getDefaultRoleForTab(),
                centre: '',
                centres: [],
                all_centres: false,
                fournisseur: ''
            });
            fetchData();
        } catch (error) {
            console.error('Error creating user:', error);
            setFormError(error.response?.data?.error || t('users.page.createError'));
        }
    };

    const openEditDialog = (user) => {
        setFormError('');
        setEditingUserId(user.id);
        const centresIds = (user.centres_details || user.centres || []).map(c => (typeof c === 'object' ? c.id : c));
        const deduped = Array.from(new Set(centresIds.filter(Boolean).map(String)));
        const primaryCentre = user.centre?.id || user.centre || user.centre_id || deduped[0] || '';
        setFormData({
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            email: user.email || '',
            role: user.role || 'RESPONSABLE',
            centre: primaryCentre ? String(primaryCentre) : '',
            centres: deduped.length ? deduped : (primaryCentre ? [String(primaryCentre)] : []),
            all_centres: (user.role === 'ADMIN') ? true : Boolean(user.all_centres),
            fournisseur: user.fournisseur || ''
        });
        setIsAddDialogOpen(true);
    };

    const openViewDialog = (user) => {
        setSelectedUser(user);
        setIsViewDialogOpen(true);
    };

    const getCentreNames = (user) => {
        if (!user) return [];
        if (user.centre_name) return [user.centre_name];
        if (user.centre?.name) return [user.centre.name];

        if (Array.isArray(user.centres_details) && user.centres_details.length > 0) {
            return user.centres_details.map(c => c?.name).filter(Boolean);
        }

        const centreIds = [];
        if (user.centre) centreIds.push(user.centre);
        if (user.centre_id) centreIds.push(user.centre_id);
        if (Array.isArray(user.centres) && user.centres.length > 0) {
            centreIds.push(...user.centres);
        }
        const ids = Array.from(new Set(centreIds.filter(Boolean).map(String)));
        if (ids.length === 0) return [];

        const byId = new Map((centres || []).map(c => [String(c.id), c.name]));
        return ids.map(id => byId.get(id)).filter(Boolean);
    };

    const getCentreDisplay = (user) => {
        if (user?.all_centres) {
            return 'Tous les centres';
        }
        if (user.role === 'FOURNISSEUR' && user.fournisseur_name) {
            return user.fournisseur_name;
        }
        const names = getCentreNames(user);
        if (!names.length) return '-';
        return names.join(', ');
    };

    const getRoleIcon = (role) => {
        switch (role) {
            case 'ADMIN':
                return <Shield size={16} className="role-icon admin" />;
            case 'RESPONSABLE':
                return <UserCheck size={16} className="role-icon responsable" />;
            case 'FOURNISSEUR':
                return <Truck size={16} className="role-icon fournisseur" />;
            case 'COMPTABLE':
                return <Building2 size={16} className="role-icon comptable" />;
            case 'LOGISTIQUE_COORDINATOR':
                return <Truck size={16} className="role-icon logistique" />;
            case 'MANAGER':
                return <Users size={16} className="role-icon manager" />;
            default:
                return <Users size={16} className="role-icon" />;
        }
    };

    const getRoleLabel = (role) => {
        switch (role) {
            case 'ADMIN':
                return 'Administrateur';
            case 'RESPONSABLE':
                return 'Responsable';
            case 'FOURNISSEUR':
                return 'Fournisseur';
            case 'COMPTABLE':
                return t('roles.comptable');
            case 'LOGISTIQUE_COORDINATOR':
                return t('roles.logistique_coordinator');
            case 'MANAGER':
                return t('roles.manager');
            default:
                return role;
        }
    };

    const getFilteredUsers = () => {
        if (isManager) {
            return users;
        }
        if (isAdmin && activeTab === 'fournisseurs') {
            return fournisseurUsers;
        }
        if (isAdmin && activeTab === 'admins') {
            return adminUsers;
        }
        if (isAdmin && activeTab === 'comptables') {
            return comptableUsers;
        }
        if (isAdmin && activeTab === 'logistique') {
            return logistiqueUsers;
        }
        if (isAdmin && activeTab === 'managers') {
            return managerUsers;
        }
        return responsables;
    };

    const handleDelete = async (user) => {
        try {
            if (canManageUsers) {
                await userService.deleteUser(user.id);
            } else if (isResponsable) {
                await userService.deleteUser(user.id);
            }
            setDeleteConfirmUser(null);
            setFormError('');
            toast.success(t('users.page.deleteSuccess'));
            await fetchData();
        } catch (error) {
            console.error('Error deleting user:', error);
            const errorMsg = error.response?.data?.error || t('users.page.errors.deleteFailed');
            setFormError(errorMsg);
            toast.error(errorMsg);
        }
    };

    const handleReactivate = async (user) => {
        try {
            await userService.reactivateUser(user.id);
            toast.success(t('users.page.updateSuccess') || 'Utilisateur réactivé');
            await fetchData();
        } catch (error) {
            const errorMsg = error.response?.data?.error || t('users.page.errors.updateFailed') || 'Erreur';
            toast.error(errorMsg);
        }
    };

    const handleHardDelete = async (user) => {
        try {
            await userService.hardDeleteUser(user.id);
            setHardDeleteConfirmUser(null);
            toast.success(t('users.page.deleteSuccess'));
            await fetchData();
        } catch (error) {
            const errorMsg = error.response?.data?.error || t('users.page.errors.deleteFailed');
            setFormError(errorMsg);
            toast.error(errorMsg);
        }
    };

    const handleSendCredentials = async (user) => {
        try {
            await userService.sendCredentialsEmail(user.id);
            toast.success('Email avec identifiants envoyé avec succès');
        } catch (error) {
            const errorMsg = error.response?.data?.error || "Erreur lors de l'envoi de l'email";
            toast.error(errorMsg);
        }
    };

    const handleSendPasswordReset = async (user) => {
        try {
            await userService.sendPasswordResetEmail(user.id);
            toast.success('Email de réinitialisation envoyé avec succès');
        } catch (error) {
            const errorMsg = error.response?.data?.error || "Erreur lors de l'envoi de l'email";
            toast.error(errorMsg);
        }
    };

    const handleSearch = () => {
        setCurrentPage(1);
        setAppliedSearchTerm(searchTerm);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const filteredUsers = getFilteredUsers();
    const canCreateUser = canCreate('users');

    return (
        <div className="users-page">
            <div className="page-header-section">
                <div className="header-titles">
                    <h1 className="page-title" style={{ margin: 0, lineHeight: '1.2', fontSize: 'var(--font-size-3xl)' }}>
                        <Users size={32} />
                        {t('users.page.title')}
                    </h1>
                    <p className="page-subtitle">
                        {(isAdmin || isManager)
                            ? t('users.page.subtitle.admin')
                            : t('users.page.subtitle.responsable')}
                    </p>
                </div>
                {(canCreate('users') || isManager) && (
                    <button type="button" onClick={() => {
                        setEditingUserId(null);
                        setFormData({
                            first_name: '',
                            last_name: '',
                            email: '',
                            role: getDefaultRoleForTab(),
                            all_centres: false,
                            fournisseur: '',
                            centre: '',
                            centres: []
                        });
                        setIsAddDialogOpen(true);
                    }} className="btn-add">
                        <Plus size={20} />
                        {(isAdmin || isManager) ? t('users.page.addUser') : t('users.page.addTrainer')}
                    </button>
                )}
            </div>

            {canManageUsersAsAdmin && (
                isMobile ? (
                    <div className="mobile-user-type-filter">
                        <select
                            value={activeTab}
                            onChange={(e) => {
                                setCurrentPage(1);
                                setActiveTab(e.target.value);
                            }}
                            className="mobile-user-type-select"
                            aria-label="Type d'utilisateur"
                        >
                            <option value="responsables">{t('roles.bu_coordinator')}</option>
                            <option value="comptables">{t('roles.comptable')}</option>
                            <option value="logistique">{t('roles.logistique_coordinator')}</option>
                            <option value="managers">{t('roles.manager')}</option>
                            <option value="fournisseurs">{t('roles.fournisseur')}</option>
                            <option value="admins">{t('roles.admin')}</option>
                        </select>
                    </div>
                ) : (
                    <div className="tabs">
                        <button
                            type="button"
                            className={`tab ${activeTab === 'responsables' ? 'active' : ''}`}
                            onClick={() => {
                                setCurrentPage(1);
                                setActiveTab('responsables');
                            }}
                        >
                            <UserCheck size={16} />
                            {t('roles.bu_coordinator')}
                        </button>
                        <button
                            type="button"
                            className={`tab ${activeTab === 'comptables' ? 'active' : ''}`}
                            onClick={() => {
                                setCurrentPage(1);
                                setActiveTab('comptables');
                            }}
                        >
                            <Building2 size={16} />
                            {t('roles.comptable')}
                        </button>
                        <button
                            type="button"
                            className={`tab ${activeTab === 'logistique' ? 'active' : ''}`}
                            onClick={() => {
                                setCurrentPage(1);
                                setActiveTab('logistique');
                            }}
                        >
                            <Truck size={16} />
                            {t('roles.logistique_coordinator')}
                        </button>
                        <button
                            type="button"
                            className={`tab ${activeTab === 'managers' ? 'active' : ''}`}
                            onClick={() => {
                                setCurrentPage(1);
                                setActiveTab('managers');
                            }}
                        >
                            <Users size={16} />
                            {t('roles.manager')}
                        </button>
                        <button
                            type="button"
                            className={`tab ${activeTab === 'fournisseurs' ? 'active' : ''}`}
                            onClick={() => {
                                setCurrentPage(1);
                                setActiveTab('fournisseurs');
                            }}
                        >
                            <Truck size={16} />
                            {t('roles.fournisseur')}
                        </button>
                        <button
                            type="button"
                            className={`tab ${activeTab === 'admins' ? 'active' : ''}`}
                            onClick={() => {
                                setCurrentPage(1);
                                setActiveTab('admins');
                            }}
                        >
                            <Shield size={16} />
                            {t('roles.admin')}
                        </button>
                    </div>
                )
            )}


            <div className="filters-section">
                <div className="search-bar">
                    <Search size={20} className="search-icon" />
                    <input
                        type="text"
                        placeholder={t('users.page.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                        }}
                        className="search-input"
                    />
                    {searchTerm && (
                        <button
                            className="search-clear-btn"
                            onClick={() => {
                                setSearchTerm('');
                                setCurrentPage(1);
                                setAppliedSearchTerm('');
                            }}
                            title={t('common.clearSearch')}
                        >
                            <X size={16} />
                        </button>
                    )}
                </div>

                {hasMultipleCentres && (
                    <div className="filter-group">
                        <Filter size={18} />
                        <select
                            value={selectedCentre}
                            onChange={(e) => {
                                setSelectedCentre(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="filter-select"
                        >
                            <option value="">{allCentresLabel}</option>
                            {availableCentres.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="filter-group">
                    <button
                        type="button"
                        className="btn-search"
                        onClick={handleSearch}
                    >
                        <Search size={16} />
                        {t('users.page.searchButton')}
                    </button>
                    <label className="toggle-inactive-label">
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={(e) => {
                                setShowInactive(e.target.checked);
                                setCurrentPage(1);
                            }}
                            className="toggle-inactive-checkbox"
                        />
                        <span className="toggle-inactive-text">{t('users.page.showInactive')}</span>
                    </label>
                </div>
            </div>

            <div className="ui-fixed-list">
                <div className="ui-fixed-list-scroll overflow-auto">
                    <div className="table-container">
                        <table className={`data-table ${isMobile ? 'mobile-table' : ''} ${isAdmin ? 'users-notifications-table' : ''}`}>
                            {isMobile ? (
                                <thead className="mobile-thead">
                                    <tr>
                                        <th className="mobile-name-col">{t('users.page.table.name')}</th>
                                        <th className="mobile-actions-col">{t('users.page.table.actions')}</th>
                                    </tr>
                                </thead>
                            ) : (
                                <thead>
                                    <tr>
                                        <th>{t('users.page.table.name')}</th>
                                        <th>{t('users.page.table.email')}</th>
                                        <th>{t('users.page.table.role')}</th>
                                        <th>{t('users.page.table.center')}</th>
                                        <th>{t('users.page.table.status')}</th>
                                        {isAdmin && <th>{t('profile.notificationsTitle')}</th>}
                                        <th className="actions-column">{t('users.page.table.actions')}</th>
                                    </tr>
                                </thead>
                            )}
                            <tbody>
                                {filteredUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan={isMobile ? 2 : isAdmin ? 7 : 6} className="empty-state">
                                            <Users size={48} className="empty-icon" />
                                            <h3>{t('users.page.empty.title')}</h3>
                                            <p>
                                                {isAdmin
                                                    ? t('users.page.empty.textAdmin')
                                                    : t('users.page.empty.textResponsable')}
                                            </p>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredUsers.map((user) => (
                                        isMobile ? (
                                            <tr key={user.id} className="mobile-row">
                                                <td className="mobile-name-cell">
                                                    <div className="mobile-user-name">
                                                        {user.first_name} {user.last_name}
                                                    </div>
                                                    {isAdmin && renderNotificationPreferences(user)}
                                                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                                                        {user.is_active ? t('status.active') : t('status.inactive')}
                                                    </span>
                                                </td>
                                                <td className="mobile-actions-cell">
                                                    <button className="action-btn action-view" onClick={() => openViewDialog(user)} title={t('users.page.actions.viewDetails')}><Eye size={16} /></button>
                                                    {canEdit('users') && (
                                                        <button className="action-btn action-edit" onClick={() => openEditDialog(user)} title={t('common.edit')}><Edit2 size={16} /></button>
                                                    )}
                                                    {isAdmin && user.is_active && user.id !== currentUser.id && (
                                                        <>
                                                            <button className="action-btn action-edit" onClick={() => handleSendCredentials(user)} title="Envoyer les identifiants"><Key size={16} /></button>
                                                            <button className="action-btn action-edit" onClick={() => handleSendPasswordReset(user)} title="Envoyer réinitialisation"><Mail size={16} /></button>
                                                        </>
                                                    )}
                                                    {showInactive && !user.is_active && canEdit('users') && user.id !== currentUser.id && (
                                                        <button className="action-btn action-edit" onClick={() => handleReactivate(user)} title={t('common.activate') || 'Réactiver'}><RefreshCcw size={16} /></button>
                                                    )}
                                                    {canDelete('users') && user.id !== currentUser.id && user.is_active && (
                                                        <button className="action-btn action-delete" onClick={() => setDeleteConfirmUser(user)} title={t('users.page.actions.delete')}><Trash2 size={16} /></button>
                                                    )}
                                                    {isAdmin && showInactive && !user.is_active && user.id !== currentUser.id && (
                                                        <button className="action-btn action-delete" onClick={() => setHardDeleteConfirmUser(user)} title={t('users.page.actions.delete') || 'Supprimer définitivement'}><Trash2 size={16} /></button>
                                                    )}
                                                </td>
                                            </tr>
                                        ) : (
                                            <tr key={user.id}>
                                                <td className="name-cell">
                                                    <div className="user-name">
                                                        <div className="user-avatar-small">
                                                            {user.first_name?.[0]}{user.last_name?.[0]}
                                                        </div>
                                                        <span>{user.first_name} {user.last_name}</span>
                                                    </div>
                                                </td>
                                                <td className="email-cell">
                                                    <a href={`mailto:${user.email}`} className="email-link">
                                                        <Mail size={14} />
                                                        {user.email}
                                                    </a>
                                                </td>
                                                <td className="role-cell">
                                                    <span className={`role-badge ${user.role?.toLowerCase()}`}>
                                                        {getRoleIcon(user.role)}
                                                        {getRoleLabel(user.role)}
                                                    </span>
                                                </td>
                                                <td className="centre-cell">
                                                    {getCentreDisplay(user)}
                                                </td>
                                                <td className="status-cell">
                                                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                                                        {user.is_active ? t('status.active') : t('status.inactive')}
                                                    </span>
                                                </td>
                                                {isAdmin && <td>{renderNotificationPreferences(user)}</td>}
                                                <td className="actions-cell">
                                                    <button className="action-btn action-view" onClick={() => openViewDialog(user)} title={t('users.page.actions.viewDetails')}><Eye size={16} /></button>
                                                    {canEdit('users') && (
                                                        <button className="action-btn action-edit" onClick={() => openEditDialog(user)} title={t('common.edit')}><Edit2 size={16} /></button>
                                                    )}
                                                    {isAdmin && user.is_active && user.id !== currentUser.id && (
                                                        <>
                                                            <button className="action-btn action-edit" onClick={() => handleSendCredentials(user)} title="Envoyer les identifiants"><Key size={16} /></button>
                                                            <button className="action-btn action-edit" onClick={() => handleSendPasswordReset(user)} title="Envoyer réinitialisation mot de passe"><Mail size={16} /></button>
                                                        </>
                                                    )}
                                                    {showInactive && !user.is_active && canEdit('users') && user.id !== currentUser.id && (
                                                        <button className="action-btn action-edit" onClick={() => handleReactivate(user)} title={t('common.activate') || 'Réactiver'}><RefreshCcw size={16} /></button>
                                                    )}
                                                    {canDelete('users') && user.id !== currentUser.id && user.is_active && (
                                                        <button className="action-btn action-delete" onClick={() => setDeleteConfirmUser(user)} title={t('users.page.actions.delete')}><Trash2 size={16} /></button>
                                                    )}
                                                    {isAdmin && showInactive && !user.is_active && user.id !== currentUser.id && (
                                                        <button className="action-btn action-delete" onClick={() => setHardDeleteConfirmUser(user)} title={t('users.page.actions.delete') || 'Supprimer définitivement'}><Trash2 size={16} /></button>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="table-footer ui-fixed-list-footer">
                    <p className="results-count">
                        {t('users.page.resultsCount', { total: totalCount, currentPage, totalPages })}
                    </p>
                    <div className="pagination">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="pagination-btn"
                        >
                            {t('common.previous')}
                        </button>
                        <div className="page-numbers">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <button
                                    key={page}
                                    onClick={() => handlePageChange(page)}
                                    className={`page-number ${page === currentPage ? 'active' : ''}`}
                                >
                                    {page}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="pagination-btn"
                        >
                            {t('common.next')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Add User Dialog */}
            {isAddDialogOpen && (
                <div className="dialog-overlay ui-dialog-overlay" onClick={() => {
                    setIsAddDialogOpen(false);
                    setEditingUserId(null);
                }}>
                    <div className="dialog-content ui-dialog-container" onClick={e => e.stopPropagation()}>
                        <div className="dialog-header ui-dialog-header">
                            <h2>
                                <Plus size={20} />
                                {editingUserId
                                    ? t('users.page.dialog.editTitle')
                                    : (isAdmin ? t('users.page.dialog.addUserTitle') : t('users.page.dialog.addTrainerTitle'))}
                            </h2>
                            <button className="dialog-close-btn" onClick={() => {
                                setIsAddDialogOpen(false);
                                setEditingUserId(null);
                            }}>
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="dialog-form">
                            {formError && (
                                <div className="error-message">
                                    <AlertCircle size={16} />
                                    {formError}
                                </div>
                            )}
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="ui-label">{t('users.page.form.firstName')} *</label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleInputChange}
                                        required
                                        placeholder={t('users.page.form.firstName')}
                                        className="ui-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="ui-label">{t('users.page.form.lastName')} *</label>
                                    <input
                                        type="text"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleInputChange}
                                        required
                                        placeholder={t('users.page.form.lastName')}
                                        className="ui-input"
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="ui-label">{t('users.page.form.email')} *</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    placeholder={t('users.page.form.emailPlaceholder')}
                                    className="ui-input"
                                />
                            </div>
                            {!editingUserId && (
                                <div className="form-info" style={{ padding: '0.75rem', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', marginBottom: '1rem' }}>
                                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <AlertCircle size={16} />
                                        Un mot de passe sera généré automatiquement et envoyé par email à l'utilisateur.
                                    </p>
                                </div>
                            )}
                            {canManageUsers && (
                                <div className="form-group">
                                    <label className="ui-label">{t('users.page.form.role')} *</label>
                                    <select
                                        name="role"
                                        value={formData.role}
                                        onChange={handleInputChange}
                                        required
                                        className="ui-select"
                                    >
                                        {isAdmin && (
                                            <option value="ADMIN">{t('roles.admin')}</option>
                                        )}
                                        <option value="BU_COORDINATOR">{t('roles.bu_coordinator')}</option>
                                        <option value="MANAGER">{t('roles.manager')}</option>
                                        <option value="COMPTABLE">{t('roles.comptable')}</option>
                                        <option value="LOGISTIQUE_COORDINATOR">{t('roles.logistique_coordinator')}</option>
                                        <option value="FOURNISSEUR">{t('roles.fournisseur') || 'Fournisseur'}</option>
                                    </select>
                                </div>
                            )}
                            {canManageUsers && formData.role === 'FOURNISSEUR' && (
                                <div className="form-group">
                                    <label className="ui-label">{t('consommables.table.supplier')} *</label>
                                    <select
                                        name="fournisseur"
                                        value={formData.fournisseur}
                                        onChange={handleInputChange}
                                        required
                                        className="ui-select"
                                    >
                                        <option value="">{t('consommables.dialog.form.supplierPlaceholder')}</option>
                                        {fournisseurs.map(f => (
                                            <option key={f.id} value={f.id}>{f.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {isAdmin && (formData.role === 'MANAGER' || formData.role === 'COMPTABLE') && (
                                <div className="form-group">
                                    <label className="toggle-inactive-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                            type="checkbox"
                                            checked={Boolean(formData.all_centres)}
                                            onChange={handleAllCentresChange}
                                            className="toggle-inactive-checkbox"
                                        />
                                        <span className="toggle-inactive-text">Tous les centres</span>
                                    </label>
                                </div>
                            )}
                            {/* Centres selection */}
                            {isAdmin && formData.role === 'ADMIN' ? (
                                <div className="form-group">
                                    <label className="ui-label">{t('users.page.form.centers')} *</label>
                                    <input
                                        type="text"
                                        value={allCentresLabel}
                                        disabled
                                        className="disabled-input"
                                    />
                                </div>
                            ) : (canManageUsers || availableCentres.length > 1) ? (
                                <div className="form-group">
                                    <label className="ui-label">{t('users.page.form.centers')} *</label>
                                    <MultiSelectDropdown
                                        options={availableCentres.map(c => ({ value: c.id, label: c.name }))}
                                        value={formData.centres}
                                        onChange={handleCentresMultiSelectChange}
                                        placeholder={t('users.page.form.centersPlaceholder')}
                                        disabled={Boolean(formData.all_centres)}
                                        className="ui-select"
                                    />
                                </div>
                            ) : (availableCentres.length === 1) && formData.role !== 'ADMIN' ? (
                                <div className="form-group">
                                    <label className="ui-label">{t('users.center')}</label>
                                    <input
                                        type="text"
                                        value={availableCentres[0]?.name || ''}
                                        disabled
                                        className="disabled-input"
                                    />
                                    <input type="hidden" name="centre" value={formData.centre} />
                                </div>
                            ) : null}
                            <div className="dialog-actions ui-dialog-actions">
                                <button type="button" className="btn-secondary ui-btn-secondary" onClick={() => {
                                    setIsAddDialogOpen(false);
                                    setEditingUserId(null);
                                }}>
                                    {t('common.cancel')}
                                </button>
                                <button type="submit" className="btn-primary ui-btn-primary">
                                    {editingUserId ? t('common.save') : t('users.page.form.create')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View User Dialog */}
            {isViewDialogOpen && selectedUser && (
                <div className="dialog-overlay ui-dialog-overlay" onClick={() => setIsViewDialogOpen(false)}>
                    <div className="view-dialog-container ui-dialog-container" onClick={e => e.stopPropagation()}>
                        <div className="dialog-header ui-dialog-header">
                            <h2>
                                <Eye size={20} />
                                {t('users.page.viewDialog.title')}
                            </h2>
                            <button className="dialog-close-btn" onClick={() => setIsViewDialogOpen(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="user-details" style={{ display: 'block', minHeight: '300px', background: 'white' }}>
                            {/* <div style={{ padding: '10px', background: '#f0f0f0', marginBottom: '10px' }}>
                                DEBUG: User: {selectedUser?.first_name} {selectedUser?.last_name}
                            </div> */}
                            <div className="user-header">
                                <div className="user-avatar-large">
                                    {selectedUser.first_name?.[0]}{selectedUser.last_name?.[0]}
                                </div>
                                <div className="user-info">
                                    <h3>{selectedUser.first_name} {selectedUser.last_name}</h3>
                                    <span className={`role-badge ${selectedUser.role?.toLowerCase()}`}>
                                        {getRoleIcon(selectedUser.role)}
                                        {getRoleLabel(selectedUser.role)}
                                    </span>
                                    {selectedUser.role === 'FOURNISSEUR' && selectedUser.fournisseur_name && (
                                        <div className="user-supplier-name" style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
                                            <Truck size={14} style={{ marginRight: '0.5rem' }} />
                                            {selectedUser.fournisseur_name}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="details-grid">
                                <div className="detail-item">
                                    <label>{t('users.email')}</label>
                                    <p>
                                        <Mail size={14} />
                                        {selectedUser.email}
                                    </p>
                                </div>
                                <div className="detail-item">
                                    <label>{t('users.center')}</label>
                                    <p>
                                        <Building2 size={14} />
                                        {getCentreDisplay(selectedUser)}
                                    </p>
                                </div>
                                {selectedUser.centres_details && selectedUser.centres_details.length > 0 && (
                                    <div className="detail-item full-width">
                                        <label>{t('users.assignedCenters')}</label>
                                        <div className="centres-list">
                                            {selectedUser.centres_details.map(centre => (
                                                <span key={centre.id} className="centre-tag">
                                                    {centre.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div className="detail-item">
                                    <label>{t('users.signupDate')}</label>
                                    <p>{new Date(selectedUser.date_joined).toLocaleDateString(i18n.language)}</p>
                                </div>
                                <div className="detail-item">
                                    <label>{t('users.status')}</label>
                                    <span className={`status-badge ${selectedUser.is_active ? 'active' : 'inactive'}`}>
                                        {selectedUser.is_active ? t('status.active') : t('status.inactive')}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="dialog-actions ui-dialog-actions">
                            <button className="btn-secondary ui-btn-secondary" onClick={() => setIsViewDialogOpen(false)}>
                                {t('common.close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            {deleteConfirmUser && (
                <div className="dialog-overlay" onClick={() => setDeleteConfirmUser(null)}>
                    <div className="dialog-container confirm-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="dialog-header">
                            <div className="dialog-title">
                                <AlertCircle size={24} />
                                <h2>{t('users.page.deleteConfirm.title')}</h2>
                            </div>
                            <button className="dialog-close" onClick={() => setDeleteConfirmUser(null)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="dialog-content">
                            <p>{t('users.page.deleteConfirm.message', { name: `${deleteConfirmUser.first_name} ${deleteConfirmUser.last_name}` })}</p>
                            {formError && (
                                <div className="error-message">
                                    <AlertCircle size={16} />
                                    {formError}
                                </div>
                            )}
                        </div>
                        <div className="dialog-actions">
                            <button className="btn-secondary" onClick={() => setDeleteConfirmUser(null)}>
                                {t('common.cancel')}
                            </button>
                            <button className="btn-danger" onClick={() => handleDelete(deleteConfirmUser)}>
                                {t('users.page.deleteConfirm.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {hardDeleteConfirmUser && (
                <div className="dialog-overlay" onClick={() => setHardDeleteConfirmUser(null)}>
                    <div className="dialog-container confirm-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="dialog-header">
                            <div className="dialog-title">
                                <AlertCircle size={24} />
                                <h2>{t('users.page.deleteConfirm.title')}</h2>
                            </div>
                            <button className="dialog-close" onClick={() => setHardDeleteConfirmUser(null)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="dialog-content">
                            <p>{t('users.page.deleteConfirm.message', { name: `${hardDeleteConfirmUser.first_name} ${hardDeleteConfirmUser.last_name}` })}</p>
                            {formError && (
                                <div className="error-message">
                                    <AlertCircle size={16} />
                                    {formError}
                                </div>
                            )}
                        </div>
                        <div className="dialog-actions">
                            <button className="btn-secondary" onClick={() => setHardDeleteConfirmUser(null)}>
                                {t('common.cancel')}
                            </button>
                            <button className="btn-danger" onClick={() => handleHardDelete(hardDeleteConfirmUser)}>
                                {t('users.page.deleteConfirm.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersPage;
